import { useEffect } from "react";
import L from "leaflet";
import { CircleMarker, MapContainer, Marker, Polyline, TileLayer, Tooltip, useMap, useMapEvents } from "react-leaflet";
import { env } from "../config/env";

// Provider-agnostic map contract. Leaflet/OSM implementation today; a Google Maps implementation can
// satisfy the same props later (switch on env.mapProvider) without touching pages.
export type MapMarker = {
  id: string;
  lat: number;
  lng: number;
  kind: "pickup" | "destination" | "responder" | "responder-busy" | "responder-offline" | "me" | "vehicle";
  label?: string;
  selected?: boolean;
};

export type MapViewProps = {
  center?: { lat: number; lng: number };
  zoom?: number;
  markers?: MapMarker[];
  route?: [number, number][];
  routeDashed?: boolean;
  onPick?: (lat: number, lng: number) => void;
  fit?: boolean;
  className?: string;
  testId?: string;
};

const STYLE: Record<MapMarker["kind"], { color: string; fill: string; r: number }> = {
  pickup: { color: "#B45309", fill: "#FFC400", r: 10 },
  destination: { color: "#024634", fill: "#036248", r: 9 },
  responder: { color: "#024634", fill: "#10B981", r: 8 },
  "responder-busy": { color: "#92400E", fill: "#FBBF2E", r: 8 },
  "responder-offline": { color: "#475569", fill: "#CBD5E1", r: 7 },
  me: { color: "#1E3A8A", fill: "#3B82F6", r: 8 },
  vehicle: { color: "#003F32", fill: "#FFC400", r: 8 },
};

const VAN_ICON = L.icon({ iconUrl: "/ptv-van-icon.png", iconSize: [56, 27], iconAnchor: [28, 22], className: "fp-van-marker" });

function Picker({ onPick }: { onPick?: (lat: number, lng: number) => void }) {
  useMapEvents({ click: (e) => onPick?.(e.latlng.lat, e.latlng.lng) });
  return null;
}

function Fit({ points, center }: { points: [number, number][]; center?: { lat: number; lng: number } }) {
  const map = useMap();
  const key = points.map((p) => p.join(",")).join("|");
  useEffect(() => {
    if (points.length > 1) map.fitBounds(points, { padding: [40, 40], maxZoom: 15 });
    else if (points.length === 1) map.setView(points[0], Math.max(map.getZoom(), 14));
    else if (center) map.setView([center.lat, center.lng]);
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

export function MapView({ center = env.defaultCenter, zoom = 12, markers = [], route, routeDashed, onPick, fit = true, className = "h-72", testId = "map-view" }: MapViewProps) {
  const points = markers.map((m) => [m.lat, m.lng] as [number, number]);
  return (
    <div className={`relative overflow-hidden rounded-2xl border border-slate-200 ${className}`} data-testid={testId}>
      <MapContainer center={[center.lat, center.lng]} zoom={zoom} className="h-full w-full" scrollWheelZoom={false}>
        <TileLayer url={env.mapTileUrl} attribution={env.mapAttribution} />
        {markers.map((m) => {
          if (m.kind === "vehicle") {
            return (
              <Marker key={m.id} position={[m.lat, m.lng]} icon={VAN_ICON}>
                {m.label && <Tooltip direction="top" offset={[0, -18]}>{m.label}</Tooltip>}
              </Marker>
            );
          }
          const s = STYLE[m.kind];
          return (
            <CircleMarker key={m.id} center={[m.lat, m.lng]} radius={m.selected ? s.r + 4 : s.r}
              pathOptions={{ color: m.selected ? "#0F172A" : s.color, weight: m.selected ? 3 : 2, fillColor: s.fill, fillOpacity: 0.95 }}>
              {m.label && <Tooltip direction="top" offset={[0, -6]}>{m.label}</Tooltip>}
            </CircleMarker>
          );
        })}
        {route && route.length > 1 && (
          <Polyline positions={route} pathOptions={{ color: "#036248", weight: 4, dashArray: routeDashed ? "6 8" : undefined }} />
        )}
        <Picker onPick={onPick} />
        {fit && <Fit points={points} center={center} />}
      </MapContainer>
    </div>
  );
}
